import { createSlice } from '@reduxjs/toolkit';
import { AxiosResponse } from 'axios';
import axiosInstance from '../../utils/axios';
import amplifyConfig from '../../config';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { AppThunk } from '../store';
import type { DateComparisonOperator, ErrorResponse, InventoryStatus, InventoryStatusState, JobId } from '../../types/inventoryStatus';

const initialState: InventoryStatusState = {
  isLoading: false,
  hasError: false,
  serverMessage: undefined,
  inventoryStatusRowData: [],
  jobIdOptions: null,
};

const slice = createSlice({
  name: 'InventoryStatus',
  initialState,
  reducers: {
    startLoading(state: InventoryStatusState) {
      state.isLoading = true;
    },

    hasError(state: InventoryStatusState, action: PayloadAction<string>) {
      // console.log(action.payload.response);
      const message = action.payload;
      state.isLoading = false;
      state.hasError = true;
      state.serverMessage = message;
    },

    resetMessage(state: InventoryStatusState) {
      state.isLoading = false;
      state.hasError = false;
      state.serverMessage = undefined;
    },

    setServerMessage(state: InventoryStatusState, action: PayloadAction<string>): void {
      state.isLoading = false;
      state.serverMessage = action.payload;
    },

    getInventoryStatuss(state: InventoryStatusState, action: PayloadAction<InventoryStatus[]>) {
      state.isLoading = false;
      state.hasError = false;

      const inventoryStatuss = action.payload;
      // ステータス一覧からジョブID一覧を生成する
      const jobIds: number[] = inventoryStatuss.map((status) => status.jobId).filter((jobid, idx, jobids) => jobids.indexOf(jobid) === idx);
      const jobIdOptions: JobId[] = [{ jobId: undefined }].concat(jobIds.map((jobid) => ({ jobId: jobid })));

      state.inventoryStatusRowData = inventoryStatuss;
      if (!state.jobIdOptions) {
        state.jobIdOptions = jobIdOptions;
      }
    },
  },
});

export const { reducer, actions } = slice;

export const resetMessage =
  (): AppThunk =>
  (dispatch): void => {
    dispatch(slice.actions.resetMessage());
  };

export const getInventoryStatuss =
  (): AppThunk =>
  async (dispatch): Promise<void> => {
    dispatch(slice.actions.startLoading());

    try {
      const response = await axiosInstance.get<InventoryStatus[]>(amplifyConfig.apiGateway.URL + '/usercheck/checkJobDetails');
      if (response.data.length === 0) {
        const message: string = 'データがありません';
        dispatch(slice.actions.hasError(message));
      } else {
        dispatch(slice.actions.getInventoryStatuss(response.data));
      }
    } catch (err) {
      if (err.isAxiosError) {
        // err: AxiosError
        if (err.response) {
          const serverResponse: ErrorResponse = err.response.data;
          dispatch(slice.actions.hasError(serverResponse.message));
        } else {
          dispatch(slice.actions.hasError(err.message));
        }
      } else {
        // err: Error
        dispatch(slice.actions.hasError(err.message));
      }
    }
  };

export const searchInventoryStatussByDate =
  (date: string, operator: DateComparisonOperator): AppThunk =>
  async (dispatch): Promise<void> => {
    dispatch(slice.actions.startLoading());

    // console.log(date, operator);
    try {
      const response = await axiosInstance.get<InventoryStatus[]>(amplifyConfig.apiGateway.URL + '/usercheck/checkJobDetails', {
        params: {
          date: date,
          condition: operator,
        },
      });
      if (response.data.length === 0) {
        const message: string = '検索条件に合うデータがありません';
        dispatch(slice.actions.hasError(message));
      } else {
        dispatch(slice.actions.getInventoryStatuss(response.data));
      }
    } catch (err) {
      if (err.isAxiosError) {
        // err: AxiosError
        if (err.response) {
          const serverResponse: ErrorResponse = err.response.data;
          dispatch(slice.actions.hasError(serverResponse.message));
        } else {
          dispatch(slice.actions.hasError(err.message));
        }
      } else {
        // err: Error
        dispatch(slice.actions.hasError(err.message));
      }
    }
  };

export const searchInventoryStatussByJobId =
  (jobId: number): AppThunk =>
  async (dispatch): Promise<void> => {
    dispatch(slice.actions.startLoading());

    // console.log(jobId);
    try {
      const response = await axiosInstance.get<InventoryStatus[]>(amplifyConfig.apiGateway.URL + '/usercheck/checkJobDetails', {
        params: {
          jobId: jobId,
        },
      });
      if (response.data.length === 0) {
        const message: string = '検索条件に合うデータがありません';
        dispatch(slice.actions.hasError(message));
      } else {
        dispatch(slice.actions.getInventoryStatuss(response.data));
      }
    } catch (err) {
      if (err.isAxiosError) {
        // err: AxiosError
        if (err.response) {
          const serverResponse: ErrorResponse = err.response.data;
          dispatch(slice.actions.hasError(serverResponse.message));
        } else {
          dispatch(slice.actions.hasError(err.message));
        }
      } else {
        // err: Error
        dispatch(slice.actions.hasError(err.message));
      }
    }
  };

export default slice;
